import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDriverAuth } from '../../context/DriverAuthContext';
import '../../DriverApp.css';

const fmtTime = ts => {
  if (!ts || ts === 'now') return 'الآن';
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
};

// Simple Google Translate unofficial endpoint
const translate = async (text, from, to) => {
  try {
    const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${from}&tl=${to}&dt=t&q=${encodeURIComponent(text)}`;
    const r = await fetch(url);
    const d = await r.json();
    return d?.[0]?.map(x => x?.[0]).filter(Boolean).join('') || '';
  } catch { return ''; }
};

export default function ChatPage() {
  const { orderId } = useParams();
  const { apiCall, driver } = useDriverAuth();
  const nav = useNavigate();

  const [msgs,         setMsgs]         = useState([]);
  const [input,        setInput]         = useState('');
  const [sending,      setSending]       = useState(false);
  const [error,        setError]         = useState('');
  const [translations, setTranslations]  = useState({});
  const [autoTranslate,setAutoTranslate] = useState(false);
  const [lastId,       setLastId]        = useState(null);
  const [orderInfo,    setOrderInfo]     = useState(null);
  const bottomRef = useRef(null);
  const pollRef   = useRef(null);
  const driverLang = driver?.preferred_language || 'ar';

  // Load order info for header
  useEffect(() => {
    apiCall(`/orders/active`)
      .then(d => {
        if (d?.id === orderId || d?.order?.id === orderId) {
          setOrderInfo(d.order || d);
        } else {
          setOrderInfo({ id: orderId });
        }
      })
      .catch(() => setOrderInfo({ id: orderId }));
  }, [orderId]);

  const loadMsgs = async () => {
    try {
      const params = lastId ? `?since_id=${lastId}` : '';
      const d = await apiCall(`/chat/${orderId}${params}`);
      if (d.messages?.length) {
        setMsgs(prev => {
          const ids = new Set(prev.map(m => m.id));
          const newMsgs = d.messages.filter(m => !ids.has(m.id));
          return [...prev, ...newMsgs];
        });
        const last = d.messages[d.messages.length - 1];
        if (last) setLastId(last.id);
      }
    } catch {}
  };

  useEffect(() => {
    loadMsgs();
    pollRef.current = setInterval(loadMsgs, 3000);
    return () => clearInterval(pollRef.current);
  }, [orderId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgs]);

  // Auto-translate incoming customer messages
  useEffect(() => {
    if (!autoTranslate) return;
    msgs.forEach(async m => {
      if (m.role === 'customer' && !translations[m.id]) {
        const srcLang = m.lang === 'ar' ? 'ar' : 'en';
        const tgtLang = driverLang === 'ar' ? 'ar' : 'en';
        if (srcLang === tgtLang) return;
        const t = await translate(m.content, srcLang, tgtLang);
        if (t) setTranslations(prev => ({ ...prev, [m.id]: t }));
      }
    });
  }, [msgs, autoTranslate]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true); setError('');
    try {
      const res = await apiCall(`/chat/${orderId}`, {
        method: 'POST',
        body: JSON.stringify({ content: text }),
      });
      setMsgs(p => [...p, { ...res, created_at: new Date().toISOString() }]);
      setInput('');
    } catch (e) { setError(e.message || 'فشل الإرسال'); }
    finally     { setSending(false); }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div className="drv-chat">

      {/* ── Header with back button ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 14px',
        background: 'var(--drv-surface)',
        borderBottom: '1px solid var(--drv-border)',
        flexShrink: 0,
      }}>
        <button
          onClick={() => nav('/driver/order/active')}
          style={{
            background: 'none', border: 'none', color: 'var(--drv-text)',
            cursor: 'pointer', padding: '4px 8px', borderRadius: 8,
            fontSize: 20, lineHeight: 1, flexShrink: 0,
            display: 'flex', alignItems: 'center',
          }}
          title="رجوع"
        >
          ←
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--drv-text)' }}>
            💬 محادثة مع العميل
          </div>
          {orderInfo?.order_number && (
            <div style={{ fontSize: 11, color: 'var(--drv-text2)', marginTop: 2 }}>
              {orderInfo.order_number}
            </div>
          )}
        </div>
        {orderInfo?.customer_name && (
          <div style={{
            fontSize: 12, color: 'var(--drv-text2)',
            background: 'var(--drv-surface2)', borderRadius: 8,
            padding: '3px 8px', flexShrink: 0,
          }}>
            {orderInfo.customer_name}
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="drv-chat__msgs">
        {msgs.length === 0 && (
          <div className="drv-empty" style={{ padding: 32 }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>💬</div>
            <p>ابدأ المحادثة مع العميل</p>
          </div>
        )}

        {msgs.map(m => {
          const isDriver = m.role === 'driver';
          return (
            <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isDriver ? 'flex-start' : 'flex-end' }}>
              <div className={`drv-chat__bubble drv-chat__bubble--${m.role}`}>
                {!isDriver && (
                  <div style={{ fontSize: 11, color: 'var(--drv-text2)', marginBottom: 4 }}>
                    العميل
                  </div>
                )}
                <div>{m.content}</div>

                {/* Translation */}
                {translations[m.id] && (
                  <div className="drv-chat__trans">
                    🌐 {translations[m.id]}
                  </div>
                )}

                {/* Translate button for customer messages */}
                {!isDriver && !translations[m.id] && (
                  <button
                    onClick={async () => {
                      const src = m.lang === 'ar' ? 'ar' : 'en';
                      const tgt = driverLang === 'ar' ? 'ar' : 'en';
                      if (src !== tgt) {
                        const t = await translate(m.content, src, tgt);
                        if (t) setTranslations(prev => ({ ...prev, [m.id]: t }));
                      }
                    }}
                    style={{ fontSize: 10, background: 'none', border: 'none', color: 'rgba(148,163,184,.7)', cursor: 'pointer', marginTop: 4, padding: 0 }}
                  >
                    🌐 ترجم
                  </button>
                )}

                <div className="drv-chat__time">{fmtTime(m.created_at)}</div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef}/>
      </div>

      {/* Auto-translate toggle */}
      <div style={{ padding: '6px 14px', background: 'var(--drv-surface2)', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
        <span style={{ color: 'var(--drv-text2)' }}>🌐 ترجمة تلقائية</span>
        <div
          className={`drv-switch ${autoTranslate ? 'drv-switch--on' : ''}`}
          style={{ cursor: 'pointer', width: 40, height: 22, flexShrink: 0 }}
          onClick={() => setAutoTranslate(p => !p)}
        />
        <span style={{ color: 'var(--drv-text2)', marginRight: 'auto' }}>
          {autoTranslate ? 'مفعّلة' : 'معطّلة'}
        </span>
      </div>

      {error && <div style={{ padding: '4px 14px', color: 'var(--drv-red)', fontSize: 13 }}>{error}</div>}

      {/* Input */}
      <div className="drv-chat__input">
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder="اكتب رسالة..."
          rows={1}
        />
        <button className="drv-chat__send" onClick={send} disabled={sending || !input.trim()}>
          {sending
            ? <span className="drv-spinner drv-spinner--sm"/>
            : (
              <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 20, height: 20 }}>
                <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z"/>
              </svg>
            )
          }
        </button>
      </div>
    </div>
  );
}
